import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { patientApi, type Patient, type CreatePatientRequest, type UpdatePatientRequest } from '../api/patientApi';

// Query keys for consistent caching
export const patientKeys = {
  all: ['patients'] as const,
  lists: () => [...patientKeys.all, 'list'] as const,
  list: (filters: any) => [...patientKeys.lists(), { filters }] as const,
  details: () => [...patientKeys.all, 'detail'] as const,
  detail: (id: string) => [...patientKeys.details(), id] as const,
};

// Get all patients
export const usePatients = () => {
  return useQuery({
    queryKey: patientKeys.lists(),
    queryFn: patientApi.getPatients,
  });
};

// Get single patient
export const usePatient = (id: string) => {
  return useQuery({
    queryKey: patientKeys.detail(id),
    queryFn: () => patientApi.getPatient(id),
    enabled: !!id, // Only run if ID exists
  });
};

// Create patient mutation
export const useCreatePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patientApi.createPatient,
    onSuccess: (newPatient: Patient) => {
      // Update the patients list cache
      queryClient.setQueryData(patientKeys.lists(), (oldData: Patient[] | undefined) => {
        return oldData ? [...oldData, newPatient] : [newPatient];
      });
      
      // Optionally invalidate to refetch from server
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
    },
  });
};

// Update patient mutation
export const useUpdatePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patientApi.updatePatient,
    onSuccess: (updatedPatient: Patient) => {
      // Update the specific patient cache
      queryClient.setQueryData(patientKeys.detail(updatedPatient.id), updatedPatient);
      
      // Update the patients list cache
      queryClient.setQueryData(patientKeys.lists(), (oldData: Patient[] | undefined) => {
        return oldData?.map(patient => 
          patient.id === updatedPatient.id ? updatedPatient : patient
        );
      });
    },
  });
};

// Delete patient mutation
export const useDeletePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patientApi.deletePatient,
    onSuccess: (_, deletedId: string) => {
      // Remove from patients list cache
      queryClient.setQueryData(patientKeys.lists(), (oldData: Patient[] | undefined) => {
        return oldData?.filter(patient => patient.id !== deletedId);
      });
      
      // Remove the specific patient cache
      queryClient.removeQueries({ queryKey: patientKeys.detail(deletedId) });
    },
  });
};